/**
 * FormatsSection
 *
 * Displays SRT / VTT / ASS checkboxes.
 * - Sidecar: all three forced-on (read-only).
 * - Soft embedded: SRT forced-on (read-only).
 * - Hard burn: ASS forced-on (read-only) — burn-in requires ASS/libass.
 */

import { memo } from 'react';
import { CheckSquare } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';
import type { SubtitleFormat } from '../types';

const ALL_FORMATS: { id: SubtitleFormat; label: string }[] = [
  { id: 'srt', label: 'SRT' },
  { id: 'vtt', label: 'VTT' },
  { id: 'ass', label: 'ASS' },
];

export const FormatsSection = memo(function FormatsSection() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleFormatsChange } = useSubtitles();
  const { formats, outputMode } = state;

  const isSidecar = outputMode === 'sidecar';
  const isSoftEmbedded = outputMode === 'soft_embedded';
  const isHardBurn = outputMode === 'hard_burn';

  // Sidecar, soft_embedded, and hard_burn all lock the format selector
  const formatsLocked = isSidecar || isSoftEmbedded || isHardBurn;
  const interactionDisabled = formatsLocked || isUploading || isGenerating;

  const handleToggle = (format: SubtitleFormat) => {
    if (interactionDisabled) return;
    const next = formats.includes(format)
      ? formats.filter((f) => f !== format)
      : [...formats, format];
    // Always keep at least one format selected
    if (next.length > 0) handleFormatsChange(next);
  };

  // Derive which checkbox should appear checked per mode
  const isChecked = (id: SubtitleFormat): boolean => {
    if (isSoftEmbedded) return id === 'srt';
    if (isHardBurn) return id === 'ass';
    return formats.includes(id);
  };

  return (
    <div className="panel-glass">
      <h3 className="section-heading">
        <CheckSquare className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
        <span>{t('subtitles.formats.title', { defaultValue: 'Output Formats' })}</span>
      </h3>

      {isSidecar && (
        <p className="text-xs text-white/50 mb-3 bg-black/20 border border-white/10 rounded p-2">
          {t('subtitles.formats.sidecarInfo', {
            defaultValue:
              'Sidecar mode always generates all three formats (SRT, VTT, ASS) in one ZIP.',
          })}
        </p>
      )}

      {isSoftEmbedded && (
        <p className="text-xs text-white/50 mb-3 bg-black/20 border border-white/10 rounded p-2">
          {t('subtitles.formats.softEmbeddedInfo', {
            defaultValue:
              'Soft-embedded uses SRT internally. The output is a single MP4 with subtitle tracks — no separate format files.',
          })}
        </p>
      )}

      {isHardBurn && (
        <p className="text-xs text-white/50 mb-3 bg-black/20 border border-white/10 rounded p-2">
          {t('subtitles.formats.hardBurnInfo', {
            defaultValue:
              'Hard burn-in uses ASS format to render styled text into video pixels via libass. The output is a single MP4.',
          })}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        {ALL_FORMATS.map(({ id, label }) => {
          const checked = isChecked(id);
          return (
            <label
              key={id}
              className={[
                'flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors min-h-[44px]',
                checked
                  ? 'border-purple-500/50 bg-purple-900/20 text-white'
                  : 'border-white/10 bg-black/20 text-white/60',
                interactionDisabled
                  ? 'opacity-70 cursor-not-allowed'
                  : 'hover:border-purple-500/30',
              ].join(' ')}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={interactionDisabled}
                onChange={() => handleToggle(id)}
                className="h-4 w-4 accent-purple-500"
              />
              <span className="text-sm font-medium">{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
});
